import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Check, ChevronDown, ChevronUp, Star, X } from "lucide-react";
import { getSavedPin } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewsPanel({ authMethod }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const ownerHeaders = () =>
    authMethod === "pin" ? { "x-schedule-pin": getSavedPin() } : {};

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/queue`, { headers: ownerHeaders() });
      setItems(data);
      setErr("");
    } catch (error) {
      setErr(
        error.response?.status === 401
          ? "Owner access expired — unlock again."
          : "Couldn't load reviews — try again."
      );
    }
  }, [authMethod]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const act = async (id, action) => {
    setBusy(true);
    setErr("");
    try {
      if (action === "approve") {
        await axios.post(`${API}/reviews/${id}/approve`, {}, { headers: ownerHeaders() });
      } else {
        await axios.delete(`${API}/reviews/${id}`, { headers: ownerHeaders() });
      }
      await load();
    } catch (error) {
      setErr(
        error.response?.status === 401
          ? "Owner access expired — unlock again."
          : "That didn't go through — try again."
      );
    }
    setBusy(false);
  };

  const pending = items.filter((r) => r.status === "pending");
  const approved = items.filter((r) => r.status === "approved");

  const row = (r, actions) => (
    <div key={r.id} className="flex items-start justify-between gap-4 rounded-xl bg-ink px-4 py-3" data-testid={`review-row-${r.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className={`h-3 w-3 ${i < r.rating ? "fill-accentlight text-accentlight" : "text-white/20"}`} />
          ))}
        </div>
        <p className="mt-1.5 text-sm text-white/80">&ldquo;{r.text}&rdquo;</p>
        <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">{r.name}</p>
      </div>
      {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
    </div>
  );

  return (
    <div className="mb-8 rounded-2xl border border-white/5 bg-surface" data-testid="reviews-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="reviews-panel-toggle"
        className="flex w-full items-center justify-between px-6 py-4 text-left"
      >
        <span className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.2em] text-white/60">
          <Star className="h-4 w-4 text-accentlight" />
          Reviews — {pending.length} awaiting your approval
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-white/40" />
        ) : (
          <ChevronDown className="h-4 w-4 text-white/40" />
        )}
      </button>

      {open && (
        <div className="border-t border-white/5 px-6 py-5" data-testid="reviews-panel-body">
          {err && (
            <p className="mb-3 text-xs text-red-300" data-testid="reviews-panel-error">
              {err}
            </p>
          )}

          {items.length === 0 && (
            <p className="text-sm text-white/40">No reviews submitted yet.</p>
          )}

          {pending.length > 0 && (
            <div className="space-y-2">
              {pending.map((r) =>
                row(r, [
                  <button
                    key="approve"
                    onClick={() => act(r.id, "approve")}
                    disabled={busy}
                    data-testid={`review-approve-${r.id}`}
                    aria-label="Approve review"
                    className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-accent to-accentlight px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110 disabled:opacity-60"
                  >
                    <Check className="h-3.5 w-3.5" /> Approve
                  </button>,
                  <button
                    key="reject"
                    onClick={() => act(r.id, "reject")}
                    disabled={busy}
                    data-testid={`review-reject-${r.id}`}
                    aria-label="Delete review"
                    className="flex items-center gap-1.5 rounded-full border border-red-400/40 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-red-300 transition-all hover:bg-red-500/10 disabled:opacity-60"
                  >
                    <X className="h-3.5 w-3.5" /> Reject
                  </button>,
                ])
              )}
            </div>
          )}

          {approved.length > 0 && (
            <>
              <p className="mb-2 mt-5 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/35">
                Showing on your website ({approved.length})
              </p>
              <div className="space-y-2">
                {approved.map((r) =>
                  row(r, [
                    <button
                      key="remove"
                      onClick={() => act(r.id, "reject")}
                      disabled={busy}
                      data-testid={`review-remove-${r.id}`}
                      aria-label="Remove review from website"
                      className="rounded-full border border-white/15 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-white/60 transition-colors hover:text-white disabled:opacity-60"
                    >
                      Remove
                    </button>,
                  ])
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}